import { FlexColumn, FlexResponsive, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { List, arrayMove, arrayRemove } from 'baseui/dnd-list';
import { Plus } from 'baseui/icon';
import { LabelSmall, ParagraphSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, REQUIRED_FIELD_TEXT } from '../../constants/app';
import { BOOLEAN_DEFAULT_VALUE_OPTIONS, RELATED_ATTRIBUTE_OPTIONS } from '../../constants/forms';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { useFeedback } from '../../hooks/useFeedback';
import { useFormFieldConfig } from '../../hooks/useFormFieldConfig';
import { useForms } from '../../hooks/useForms';
import { useStyles } from '../../hooks/useStyles';
import { createFormField, sortFormFields, updateFormField } from '../../services/formsService';
import {
  editFormFieldErrorState,
  editFormFieldState,
  editFormFieldTouchedState,
  editFormFieldValidState,
  formDetailFieldsState,
  selectedFormFieldState,
  selectedFormIdState,
  updateFormFieldsState,
} from '../../storage/formStates';
import { FormFieldOption, FormFieldRequest, FormFieldRequestTouched } from '../../types/forms';
import { getFieldTypeName } from '../../utils/forms';
import { FormActionsRow } from '../shared/FormActionsRow';
import StyledModal from '../shared/StyledModal';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  isOpen: boolean;
};

export function FormFieldConfigModal({ isOpen }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { isLarge } = useBreakpoints();
  const selectedFormId = useRecoilValue(selectedFormIdState);
  const [formField, setFormField] = useRecoilState(editFormFieldState);
  const [formFieldTouched, setFormFieldTouched] = useRecoilState(editFormFieldTouchedState);
  const { loading, startLoading, stopLoading } = useLoading();
  const { onTouched } = useForms<FormFieldRequest, FormFieldRequestTouched>(setFormField, setFormFieldTouched);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const formFieldError = useRecoilValue(editFormFieldErrorState);
  const formFieldValid = useRecoilValue(editFormFieldValidState);
  const resetFormField = useResetRecoilState(editFormFieldState);
  const resetFormFieldTouched = useResetRecoilState(editFormFieldTouchedState);
  const resetSelectedFormField = useResetRecoilState(selectedFormFieldState);
  const selectedFormField = useRecoilValue(selectedFormFieldState);
  const setUpdateFormFields = useSetRecoilState(updateFormFieldsState);
  const canSubmit = !formFieldError;
  const editMode = !selectedFormField?.uncreated;
  const fieldConfig = useFormFieldConfig(formField.fieldType);
  const [newOption, setNewOption] = useState({ text: '', value: '' });
  const formDetailFields = useRecoilValue(formDetailFieldsState);

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  const defaultOptionsList = useMemo(() => {
    if (fieldConfig.renderType === 'checkbox') {
      return BOOLEAN_DEFAULT_VALUE_OPTIONS;
    } else {
      if (!formField.options) return [];
      return [
        ...formField.options.map((option) => ({
          id: option.value,
          label: option.text,
        })),
      ];
    }
  }, [formField, fieldConfig]);

  if (!selectedFormField) return null;

  const clearStates = () => {
    resetFormField();
    resetFormFieldTouched();
    setNewOption({ text: '', value: '' });
  };

  const onChangeInput = (key: keyof FormFieldRequest, value: any) => {
    setFormField((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onSubmit = async () => {
    if (!selectedFormId) return;
    const sortFields = async (formFieldId: string) => {
      const fieldIds = formDetailFields.map((f) => f._id);
      const fieldIndex = fieldIds.findIndex((id) => id === formFieldId);
      var sortedFields = [];
      if (fieldIndex !== -1) {
        sortedFields = arrayMove(fieldIds, fieldIndex, formField.order - 1);
      } else {
        sortedFields = fieldIds.splice(formField.order - 1, 0, formFieldId);
      }
      if (sortedFields.length) {
        return await sortFormFields(
          selectedFormId,
          sortedFields.map((id, index) => ({ _id: id, order: index + 1 }))
        );
      }
    };
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      onCloseModal();
      setUpdateFormFields('update');
    };
    startLoading();
    if (editMode && selectedFormField) {
      const { error } = await updateFormField(selectedFormId, selectedFormField.id, {
        ...formField,
        active: formField.active === undefined ? true : formField.active,
      });
      if (!error) {
        await sortFields(selectedFormField.id);
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.formField, formField.name));
      } else {
        showFailFeedback(error);
      }
    } else if (selectedFormField) {
      const { id, error } = await createFormField(selectedFormId, {
        ...formField,
        active: formField.active === undefined ? true : formField.active,
      });
      if (!error && id) {
        await sortFields(id);
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.formField));
      } else {
        showFailFeedback(error || 'No se pudo crear el campo.');
      }
    }
    stopLoading();
  };

  const onCloseModal = () => {
    clearStates();
    resetSelectedFormField();
  };

  const onAddOption = (option: FormFieldOption) => {
    setFormField((prev) => ({
      ...prev,
      options: prev.options ? [...prev.options, option] : [option],
    }));
    setNewOption({ text: '', value: '' });
  };

  const onChangeOptions = (options: FormFieldOption[]) => {
    setFormField((prev) => ({
      ...prev,
      options: options.map((opt, index) => ({ ...opt, order: index })),
    }));
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title={(editMode ? 'Editar campo ' : 'Nuevo campo ') + '- ' + getFieldTypeName(formField.fieldType)}
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '525px',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            <LabeledInput
              label="Nombre"
              placeholder="Ingresa el nombre del campo"
              value={formField.name}
              onChange={(value) => onChangeInput('name', value)}
              onBlur={() => onTouched('nameTouched')}
              error={formFieldTouched.nameTouched && !formFieldValid.name && REQUIRED_FIELD_TEXT}
              required
            />
            {fieldConfig.showHelpText && (
              <LabeledInput
                label="Descripción"
                placeholder="Ingresa la descripción del campo"
                value={formField.help_text}
                onChange={(value) => onChangeInput('help_text', value)}
              />
            )}
            {fieldConfig.showDefault && fieldConfig.inputType !== 'select' && (
              <LabeledInput
                label="Valor por defecto"
                placeholder="Ingresa el valor por defecto del campo"
                type={fieldConfig.inputType}
                value={formField.default}
                onChange={(value) => onChangeInput('default', value)}
              />
            )}
            {fieldConfig.showDefault && fieldConfig.inputType === 'select' && (
              <EnhancedLabeledSelect
                label="Valor por defecto"
                placeholder="Ingresa el valor por defecto del campo"
                value={
                  formField.default
                    ? [
                        {
                          id:
                            fieldConfig.renderType === 'checkbox'
                              ? formField.default
                              : defaultOptionsList.find((option) => option.label === formField.default)?.id,
                        },
                      ]
                    : undefined
                }
                options={defaultOptionsList}
                onChange={(params) => {
                  if (fieldConfig.renderType === 'checkbox')
                    onChangeInput('default', (params.option?.id as string) || '');
                  else onChangeInput('default', (params.option?.label as string) || '');
                }}
                disabled={fieldConfig.renderType === 'checkbox' ? false : defaultOptionsList.length === 0}
                clearable
              />
            )}
            {fieldConfig.showPrefix && (
              <LabeledInput
                label="Prefijo"
                placeholder="Ingresa el prefijo del campo"
                value={formField.prefix}
                onChange={(value) => onChangeInput('prefix', value)}
              />
            )}
            {fieldConfig.showSufix && (
              <LabeledInput
                label="Sufijo"
                placeholder="Ingresa el sufijo del campo"
                value={formField.sufix}
                onChange={(value) => onChangeInput('sufix', value)}
              />
            )}
            {fieldConfig.showRelatedAttribute && (
              <EnhancedLabeledSelect
                label="Atributo relacionado"
                placeholder="Selecciona el atributo a relacionar"
                value={formField.relatedAttribute ? [{ id: formField.relatedAttribute }] : undefined}
                options={RELATED_ATTRIBUTE_OPTIONS}
                onChange={(params) => onChangeInput('relatedAttribute', params.option?.id)}
              />
            )}
            {fieldConfig.showOptions && (
              <FlexColumn gap={theme.sizing.scale200}>
                <LabelSmall>Opciones</LabelSmall>
                {formField.options && formField.options.length > 0 && (
                  <List
                    items={formField.options.map((option) => {
                      return (
                        <FlexColumn gap={0}>
                          <LabelSmall>{option.text}</LabelSmall>
                          <ParagraphSmall
                            margin={0}
                            color={theme.colors.contentTertiary}
                          >
                            Valor: {option.value}
                          </ParagraphSmall>
                        </FlexColumn>
                      );
                    })}
                    overrides={{
                      List: {
                        style: {
                          margin: 0,
                        },
                      },
                      Item: {
                        style: {
                          padding: theme.sizing.scale200,
                        },
                      },
                    }}
                    removable
                    onChange={({ oldIndex, newIndex }) => {
                      const newOptions = formField.options ? [...formField.options] : [];
                      onChangeOptions(
                        newIndex === -1 ? arrayRemove(newOptions, oldIndex) : arrayMove(newOptions, oldIndex, newIndex)
                      );
                    }}
                  />
                )}
                <FlexResponsive
                  centered
                  gap={theme.sizing.scale300}
                >
                  <LabeledInput
                    placeholder="Nombre"
                    value={newOption.text}
                    onChange={(value) => setNewOption((prev) => ({ ...prev, text: value }))}
                  />
                  <LabeledInput
                    placeholder="Valor"
                    value={newOption.value}
                    onChange={(value) => setNewOption((prev) => ({ ...prev, value }))}
                  />
                  <StyledButton
                    kind="secondary"
                    onClick={() => {
                      onAddOption({
                        text: newOption.text,
                        value: newOption.value,
                        order: formField.options?.length ?? 0,
                      });
                    }}
                    startEnhancer={<Plus />}
                    disabled={!newOption.text || !newOption.value}
                    full={isLarge ? true : false}
                  >
                    Agregar
                  </StyledButton>
                </FlexResponsive>
              </FlexColumn>
            )}
            <LabeledInput
              label="Orden"
              placeholder="Ingresa el orden del campo"
              type="number"
              value={formField.order}
              onChange={(value) => onChangeInput('order', value)}
              onBlur={() => onTouched('orderTouched')}
              error={
                formFieldTouched.orderTouched && !formFieldValid.order
                  ? formField.order.toString() === ''
                    ? REQUIRED_FIELD_TEXT
                    : 'Ingresa un número mayor o igual a 0'
                  : undefined
              }
              required
            />
            <Checkbox
              checked={formField.required}
              onChange={(e) => onChangeInput('required', e.target.checked)}
            >
              Requerido
            </Checkbox>
            <Checkbox
              checked={formField.pdfVisible}
              onChange={(e) => onChangeInput('pdfVisible', e.target.checked)}
            >
              Mostrar en el PDF
            </Checkbox>
            {!selectedFormField.uncreated && (
              <Checkbox
                checked={formField.active ?? true}
                onChange={(e) => onChangeInput('active', e.target.checked)}
              >
                Activo
              </Checkbox>
            )}
            {fieldConfig.showIsDeviceId && (
              <Checkbox
                checked={formField.isDeviceId ?? false}
                onChange={(e) => onChangeInput('isDeviceId', e.target.checked)}
              >
                ID equipo
              </Checkbox>
            )}
          </FlexColumn>
          <FormActionsRow
            canSubmit={canSubmit}
            loading={loading}
            editMode={editMode}
            onSubmit={onSubmit}
            onCancel={onCloseModal}
          />
        </FlexColumn>
      }
    />
  );
}
